import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleDollarSign,
} from "lucide-react";
import { scrollPageToTop } from "../../utils/scrollToTop";

export function WorkflowToolbar({ actions = [] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map(({ label, primary, onClick }) => (
        <button
          className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
            primary
              ? "bg-[#4391d1] text-white hover:bg-blue-600"
              : "border border-gray-200 bg-white text-gray-800 hover:border-[#4391d1] hover:text-[#4391d1]"
          }`}
          key={label}
          onClick={onClick}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function StatusPill({ children, tone = "neutral" }) {
  const classes = {
    neutral: "bg-gray-100 text-gray-700",
    good: "bg-emerald-50 text-emerald-700",
    warn: "bg-amber-50 text-amber-700",
    bad: "bg-red-50 text-red-700",
    info: "bg-blue-50 text-blue-700",
  };

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-black uppercase ${
        classes[tone] || classes.neutral
      }`}
    >
      {children}
    </span>
  );
}

export function InsightCard({ title, value, note, tone = "info" }) {
  const icons = {
    info: ArrowUpRight,
    good: CheckCircle2,
    warn: AlertTriangle,
    money: CircleDollarSign,
  };

  const Icon = icons[tone] || ArrowUpRight;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-gray-500">{title}</p>
          <p className="mt-3 text-3xl font-black text-[#4391d1]">{value}</p>
        </div>

        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#4391d1]">
          <Icon size={18} />
        </span>
      </div>

      <p className="mt-3 text-xs font-semibold text-gray-400">{note}</p>
    </div>
  );
}

export function PaginationControls({ pagination }) {
  if (!pagination || Number(pagination.totalItems || 0) === 0) return null;

  const pageSize = Number(pagination.pageSize || 25);
  const totalItems = Number(pagination.totalItems || 0);
  const totalPages = Math.max(1, Number(pagination.totalPages || 1));
  const page = Math.min(
    totalPages,
    Math.max(1, Number(pagination.page || 1))
  );
  const canChangePage = typeof pagination.setPage === "function";
  const canChangePageSize = typeof pagination.setPageSize === "function";
  const disabled = Boolean(pagination.disabled);

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(totalItems, page * pageSize);
  const changePage = (nextPage) => {
    pagination.setPage(nextPage);
    window.requestAnimationFrame(() => scrollPageToTop({ behavior: "smooth" }));
  };
  const changePageSize = (nextPageSize) => {
    pagination.setPageSize(nextPageSize);
    window.requestAnimationFrame(() => scrollPageToTop({ behavior: "smooth" }));
  };

  return (
    <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-4 text-sm font-bold text-gray-500 sm:flex-row sm:items-center sm:justify-between">
      <span>
        Showing {start}-{end} of {totalItems}
      </span>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-black text-gray-700"
          disabled={disabled || !canChangePageSize}
          onChange={(event) => {
            changePageSize(Number(event.target.value), event.currentTarget);
          }}
          value={pageSize}
        >
          {[5, 10, 25, 50].map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>

        <button
          className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 disabled:opacity-40"
          disabled={disabled || !canChangePage || page <= 1}
          onClick={(event) => changePage(page - 1, event.currentTarget)}
          type="button"
        >
          Prev
        </button>

        <span className="rounded-lg bg-gray-50 px-3 py-2 text-xs font-black text-gray-700">
          {page} / {totalPages}
        </span>

        <button
          className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 disabled:opacity-40"
          disabled={disabled || !canChangePage || page >= totalPages}
          onClick={(event) => changePage(page + 1, event.currentTarget)}
          type="button"
        >
          Next
        </button>
      </div>
    </div>
  );
}
